import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import axios from 'axios';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, { IBrandingSpotApi } from '@services/BrandingsService';
import EventsService from '@services/EventsService';
import { env } from 'env';
import type { IBrandingSpotDriftRow } from '@shared/utils/mapTemplateDrift';

import {
  EditorGroupId,
  EditorNodeKind,
  SpotType,
  getSpotType,
  FOCUS_TO_GROUP_IDS,
} from '../editorCategories';
import { useEditorMode } from '../EditorModeContext';
import { useEditorStyles } from '../styles';
import { useDrift } from '../DriftContext';
import AiScriptsTreeSection from '../AiScripts/AiScriptsTreeSection';
import { useSceneTreeStyles } from './styles';

type AnySpot = Record<string, unknown> & {
  ueIdentifier?: string;
  name?: string;
  isActive?: boolean;
  type?: string;
};

const getSpotTypeIcon = (type: SpotType, sx: object) => {
  if (type === 'VIDEO') {
    return <PlayArrowOutlinedIcon sx={sx} />;
  }

  return <ImageOutlinedIcon sx={sx} />;
};

const DEFAULT_LANGUAGE_CODE = 'en';

type SpotMedia = { url?: string; location?: string; previewImage?: string };

// The image a placeholder spot is currently showing — mirrors SpotInspector's
// field precedence. Image spots resolve to their content URL; video spots fall
// back to their preview image; QR spots have no thumbnail.
const getSpotThumbnailUrl = (spot: AnySpot): string | null => {
  const localizations = spot.localizations as
    | Record<string, SpotMedia | undefined>
    | undefined;
  const english = localizations?.[DEFAULT_LANGUAGE_CODE];
  const data = spot.data as SpotMedia | undefined;
  const previewImage =
    english?.previewImage ||
    data?.previewImage ||
    (typeof spot.previewImage === 'string' ? spot.previewImage : undefined);
  const type = getSpotType(spot);

  if (type === 'QR') {
    return null;
  }
  if (type === 'VIDEO') {
    return previewImage || null;
  }

  return (
    english?.url ||
    english?.location ||
    data?.url ||
    data?.location ||
    previewImage ||
    null
  );
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

interface IRailButtonProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void;
}

const RailButton = ({
  active,
  icon,
  label,
  tooltip,
  onClick,
}: IRailButtonProps) => {
  const treeStyles = useSceneTreeStyles();
  return (
    <Tooltip title={tooltip} placement="right" arrow enterDelay={300}>
      <Box
        sx={[treeStyles.railItem, active && treeStyles.railItemActive]}
        onClick={onClick}
        role="tab"
        aria-label={tooltip}
        aria-selected={active}
      >
        {icon}
        <Box component="span" sx={treeStyles.railItemLabel}>
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
};

const SceneTree = () => {
  const { t: translate } = useTranslation();
  const styles = useEditorStyles();
  const treeStyles = useSceneTreeStyles();

  const {
    selectedNode,
    selectMapAndColors,
    selectSpot,
    activeFocus,
    setActiveFocus,
    isSceneTreeCollapsed,
    toggleSceneTree,
    setAutosaveStatus,
  } = useEditorMode();

  const [collapsedGroups, setCollapsedGroups] = useState<
    Partial<Record<EditorGroupId, boolean>>
  >({});

  // Spot thumbnails whose image failed to load — fall back to the type icon.
  const [failedThumbs, setFailedThumbs] = useState<Record<string, boolean>>({});

  const toggleGroup = useCallback((id: EditorGroupId) => {
    setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const {
    roomSpots,
    setRoomSpots,
    brandingSpots,
    setBrandingSpots,
    currentRoom,
    currentBrandingId,
    isBrandingModeEnabled,
    setSelectedCustomizationUeId,
    setSelectedCustomizationTag,
    setSelectedCustomizationSpotType,
    setSelectedCustomizationTargetSize,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { spotDrift } = useDrift();

  const driftByUe = useMemo(() => {
    const map = new Map<string, IBrandingSpotDriftRow<IBrandingSpotApi>>();
    spotDrift.rows.forEach((row) => {
      const ue = normalizeUeIdentifier(row.ueIdentifier);
      if (ue) {
        map.set(ue, row);
      }
    });
    return map;
  }, [spotDrift]);

  const handleSpotSelect = useCallback(
    (spot: AnySpot, ueId: string) => {
      const type = getSpotType(spot);
      setSelectedCustomizationUeId(ueId);
      setSelectedCustomizationTag(ueId);
      setSelectedCustomizationSpotType(type);

      setSelectedCustomizationTargetSize(null);
      selectSpot(ueId);
    },
    [
      selectSpot,
      setSelectedCustomizationUeId,
      setSelectedCustomizationTag,
      setSelectedCustomizationSpotType,
      setSelectedCustomizationTargetSize,
    ],
  );

  const spots = useMemo<AnySpot[]>(() => {
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    return Array.isArray(source) ? (source as AnySpot[]) : [];
  }, [currentBrandingId, isBrandingModeEnabled, brandingSpots, roomSpots]);

  const isMapColorsSelected =
    selectedNode?.kind === EditorNodeKind.MAP_AND_COLORS;

  const selectedSpotUeId =
    selectedNode?.kind === EditorNodeKind.SPOT
      ? selectedNode.ueIdentifier
      : null;

  const selectedNodeRef = useRef<HTMLDivElement | null>(null);
  const spotsCollapsed = !!collapsedGroups[EditorGroupId.SPOTS];

  useEffect(() => {
    if (!selectedSpotUeId) {
      return;
    }
    setCollapsedGroups((prev) => {
      if (!prev[EditorGroupId.SPOTS]) {
        return prev;
      }
      return { ...prev, [EditorGroupId.SPOTS]: false };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpotUeId]);

  useEffect(() => {
    if (!selectedSpotUeId || spotsCollapsed) {
      return;
    }
    const node = selectedNodeRef.current;
    if (node) {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedSpotUeId, spotsCollapsed, spots.length]);

  const handleToggleVisibility = useCallback(
    async (e: React.MouseEvent, spot: AnySpot) => {
      e.stopPropagation();
      const nextActive = spot.isActive === false;
      const useBrandingSpots = !!currentBrandingId && isBrandingModeEnabled;

      const brandingSpotId =
        typeof (spot as { brandingSpotId?: unknown }).brandingSpotId ===
        'string'
          ? ((spot as { brandingSpotId: string }).brandingSpotId as string)
          : null;
      const roomSpotId =
        typeof (spot as { roomSpotId?: unknown }).roomSpotId === 'string'
          ? ((spot as { roomSpotId: string }).roomSpotId as string)
          : null;
      const fallbackId =
        typeof (spot as { id?: unknown }).id === 'string'
          ? ((spot as { id: string }).id as string)
          : null;

      setAutosaveStatus('saving');
      try {
        if (useBrandingSpots && currentBrandingId) {
          const id = brandingSpotId || fallbackId;
          if (!id) {
            throw new Error('Missing branding spot id for visibility toggle');
          }
          await BrandingsService.patchBrandingSpot(currentBrandingId, id, {
            isActive: nextActive,
          });
          const refreshed = await BrandingsService.getBrandingSpots(
            currentBrandingId,
            { limit: 200 },
          );
          setBrandingSpots(refreshed.spots ?? []);
        } else if (currentRoom) {
          const id = roomSpotId || fallbackId;
          if (!id) {
            throw new Error('Missing room spot id for visibility toggle');
          }

          await axios.patch(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${id}`,
            { isActive: nextActive },
          );
          const refreshed = await EventsService.getRoomSpots(
            currentRoom.roomId,
          );
          setRoomSpots(refreshed);
        }
        sendMessageToUnreal({
          action: 'SPOTS_UPDATED',
          spots: [],
        });
        setAutosaveStatus('saved');
      } catch (toggleError) {
        console.error(
          `Failed to toggle spot visibility: ${
            (toggleError as Error)?.message
          }`,
          {
            ueIdentifier:
              typeof spot.ueIdentifier === 'string' ? spot.ueIdentifier : null,
            brandingId: currentBrandingId,
            roomId: currentRoom?.roomId,
          },
        );
        setAutosaveStatus('error');
      }
    },
    [
      currentBrandingId,
      currentRoom,
      isBrandingModeEnabled,
      sendMessageToUnreal,
      setAutosaveStatus,
      setBrandingSpots,
      setRoomSpots,
    ],
  );

  if (isSceneTreeCollapsed) {
    return (
      <Box
        sx={[styles.glassPanel, styles.sceneTreeCollapsedPill]}
        onClick={toggleSceneTree}
      >
        <ViewSidebarOutlinedIcon fontSize="small" />
        <Typography sx={{ fontSize: '13px', color: 'inherit' }}>
          {translate('editorMode.scene', 'Scene')}
        </Typography>
      </Box>
    );
  }

  const visibleGroupIds = FOCUS_TO_GROUP_IDS[activeFocus];

  const renderEnvironment = () => (
    <Box key={EditorGroupId.ENVIRONMENT} sx={treeStyles.groupSection}>
      <Box sx={treeStyles.groupLabelRow}>
        <Typography sx={treeStyles.groupLabel}>
          {translate('editorMode.groupEnvironment', 'Environment')}
        </Typography>
      </Box>
      <Box
        sx={[treeStyles.node, isMapColorsSelected && treeStyles.nodeSelected]}
        onClick={selectMapAndColors}
      >
        <PaletteOutlinedIcon sx={treeStyles.nodeIcon} />
        <Typography sx={treeStyles.nodeLabel}>
          {translate('editorMode.mapAndColors', 'Map and colors')}
        </Typography>
      </Box>
      <Typography sx={treeStyles.driftNote}>
        {translate(
          'editorMode.driftEnvironmentNote',
          'Environment & colors aren’t template entities — they don’t drift.',
        )}
      </Typography>
    </Box>
  );

  const renderCoverageMarker = (ueId: string) => {
    const row = driftByUe.get(normalizeUeIdentifier(ueId));
    if (!row) {
      return null;
    }
    if (row.status === 'configured') {
      return (
        <CheckCircleOutlineOutlinedIcon
          sx={[treeStyles.coverageMarker, treeStyles.coverageConfigured]}
        />
      );
    }
    if (row.status === 'placeholder') {
      return (
        <RadioButtonUncheckedOutlinedIcon
          sx={[treeStyles.coverageMarker, treeStyles.coveragePlaceholder]}
        />
      );
    }
    return null;
  };

  const renderGhostSpotNode = (
    row: IBrandingSpotDriftRow<IBrandingSpotApi>,
    variant: 'missing' | 'orphaned',
  ) => {
    const ueId = normalizeUeIdentifier(row.ueIdentifier);
    const sourceSpot =
      variant === 'orphaned' && row.brandingSpot
        ? (row.brandingSpot as AnySpot)
        : ({ ueIdentifier: ueId, type: row.type } as AnySpot);
    const type = getSpotType(sourceSpot);
    const fallbackName =
      variant === 'orphaned' && typeof row.brandingSpot?.name === 'string'
        ? row.brandingSpot.name
        : '';
    const name = fallbackName || ueId;
    const sub =
      variant === 'missing'
        ? row.regionName ||
          translate('editorMode.driftInBuild', 'In build · not configured')
        : translate('editorMode.driftRemoved', 'Removed from build');
    const isSelected =
      !!selectedSpotUeId && ueId === normalizeUeIdentifier(selectedSpotUeId);

    return (
      <Box
        key={`${variant}:${ueId}`}
        ref={isSelected ? selectedNodeRef : undefined}
        sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
        onClick={() => ueId && handleSpotSelect(sourceSpot, ueId)}
      >
        {getSpotTypeIcon(type, treeStyles.nodeIcon)}
        <Box sx={treeStyles.ghostNodeLabel}>
          <Box sx={treeStyles.ghostNodeTitleRow}>
            <Typography sx={treeStyles.ghostNodeTitle}>{name}</Typography>
            {variant === 'missing' && row.isNew ? (
              <Box component="span" sx={treeStyles.newBadge}>
                {translate('editorMode.driftNew', 'New')}
              </Box>
            ) : null}
          </Box>
          <Typography sx={treeStyles.ghostNodeSub}>{sub}</Typography>
        </Box>
        {variant === 'missing' ? (
          <AddCircleOutlineOutlinedIcon sx={treeStyles.ghostMarkerMissing} />
        ) : (
          <ReportProblemOutlinedIcon sx={treeStyles.ghostMarkerOrphaned} />
        )}
      </Box>
    );
  };

  const renderSpots = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.SPOTS];

    const orphanRows = spotDrift.rows.filter(
      (row) => row.status === 'orphaned',
    );
    const missingRows = spotDrift.rows.filter(
      (row) => row.status === 'missing',
    );
    const orphanUeSet = new Set(
      orphanRows.map((row) => normalizeUeIdentifier(row.ueIdentifier)),
    );
    const inConfigSpots = spots.filter(
      (spot) => !orphanUeSet.has(normalizeUeIdentifier(spot.ueIdentifier)),
    );

    return (
      <React.Fragment key={EditorGroupId.SPOTS}>
        <Box sx={treeStyles.groupSection}>
          <Box
            sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
            onClick={() => toggleGroup(EditorGroupId.SPOTS)}
          >
            <ExpandMoreOutlinedIcon
              sx={[
                treeStyles.groupChevron,
                isCollapsed && treeStyles.groupChevronCollapsed,
              ]}
            />
            <Typography sx={treeStyles.groupLabel}>
              {translate('editorMode.groupSpots', 'Spots')}
            </Typography>
            {inConfigSpots.length > 0 && (
              <Typography sx={treeStyles.groupCount}>
                {inConfigSpots.length}
              </Typography>
            )}
          </Box>

          {!isCollapsed && inConfigSpots.length === 0 && (
            <Typography sx={treeStyles.emptyText}>
              {translate(
                'editorMode.noSpots',
                'No spots in this configuration',
              )}
            </Typography>
          )}

          {!isCollapsed &&
            inConfigSpots.map((spot, idx) => {
              const ueId = normalizeUeIdentifier(spot.ueIdentifier);
              const key = ueId || `spot-${idx}`;
              const type = getSpotType(spot);
              const isSelected =
                !!selectedSpotUeId &&
                ueId === normalizeUeIdentifier(selectedSpotUeId);
              const isHidden = spot.isActive === false;
              const name =
                typeof spot.name === 'string' && spot.name.length > 0
                  ? spot.name
                  : ueId || translate('adminToolbox.unnamed', 'Unnamed');
              const rawThumbUrl = getSpotThumbnailUrl(spot);
              const thumbUrl = failedThumbs[key] ? null : rawThumbUrl;

              return (
                <Tooltip
                  key={key}
                  title={name}
                  placement="right"
                  enterDelay={350}
                  arrow
                >
                  <Box
                    ref={isSelected ? selectedNodeRef : undefined}
                    sx={[
                      treeStyles.node,
                      isSelected && treeStyles.nodeSelected,
                    ]}
                    onClick={() => ueId && handleSpotSelect(spot, ueId)}
                  >
                    {thumbUrl ? (
                      <Box
                        component="img"
                        src={thumbUrl}
                        alt=""
                        sx={treeStyles.nodeThumb}
                        onError={() => {
                          setFailedThumbs((prev) => ({ ...prev, [key]: true }));
                        }}
                      />
                    ) : (
                      getSpotTypeIcon(type, treeStyles.nodeIcon)
                    )}
                    <Typography sx={treeStyles.nodeLabel}>{name}</Typography>
                    {renderCoverageMarker(ueId)}
                    <Box sx={treeStyles.nodeActions}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleToggleVisibility(e, spot)}
                        sx={[
                          treeStyles.nodeEye,
                          isHidden && treeStyles.nodeEyeHidden,
                        ]}
                        aria-label={translate(
                          'editorMode.toggleVisibility',
                          'Toggle visibility',
                        )}
                      >
                        {isHidden ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
        </Box>

        {missingRows.length > 0 && (
          <Box sx={treeStyles.groupSection}>
            <Box sx={treeStyles.groupLabelRow}>
              <Typography
                sx={[treeStyles.groupLabel, treeStyles.driftGroupLabelAmber]}
              >
                {translate(
                  'editorMode.driftMissingGroup',
                  'Missing in this config',
                )}
              </Typography>
              <Typography sx={treeStyles.groupCount}>
                {missingRows.length}
              </Typography>
            </Box>
            {missingRows.map((row) => renderGhostSpotNode(row, 'missing'))}
          </Box>
        )}

        {orphanRows.length > 0 && (
          <Box sx={treeStyles.groupSection}>
            <Box sx={treeStyles.groupLabelRow}>
              <Typography
                sx={[treeStyles.groupLabel, treeStyles.driftGroupLabelRed]}
              >
                {translate(
                  'editorMode.driftOrphanedGroup',
                  'Orphaned · removed from build',
                )}
              </Typography>
              <Typography sx={treeStyles.groupCount}>
                {orphanRows.length}
              </Typography>
            </Box>
            {orphanRows.map((row) => renderGhostSpotNode(row, 'orphaned'))}
          </Box>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={[styles.glassPanel, styles.sceneTreeWrap, { overflow: 'hidden' }]}>
      <Box sx={styles.panelHeader}>
        <Box sx={styles.panelTitleRow}>
          <LayersOutlinedIcon sx={styles.panelTitleIcon} />
          <Typography sx={styles.panelTitle}>
            {translate('editorMode.scene', 'Scene')}
          </Typography>
        </Box>
        <IconButton
          onClick={toggleSceneTree}
          sx={styles.panelIconButton}
          size="small"
          aria-label={translate('editorMode.collapseTree', 'Collapse scene')}
        >
          <ViewSidebarOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={treeStyles.railLayout}>
        <Box sx={treeStyles.rail} role="tablist">
          <RailButton
            active={activeFocus === 'environment'}
            icon={<PublicOutlinedIcon />}
            label={translate('editorMode.railMap', 'Map')}
            tooltip={translate('editorMode.focusEnvironment', 'Map & colors')}
            onClick={() => setActiveFocus('environment')}
          />
          <RailButton
            active={activeFocus === 'spots'}
            icon={<ImageOutlinedIcon />}
            label={translate('editorMode.railSpots', 'Spots')}
            tooltip={translate('editorMode.focusSpots', 'Spots & media')}
            onClick={() => setActiveFocus('spots')}
          />
          <RailButton
            active={activeFocus === 'assistant'}
            icon={<SmartToyOutlinedIcon />}
            label={translate('editorMode.railScripts', 'Scripts')}
            tooltip={translate(
              'editorMode.focusAssistant',
              'AI scripts & context',
            )}
            onClick={() => setActiveFocus('assistant')}
          />
        </Box>

        <Box sx={treeStyles.railBody}>
          {visibleGroupIds.includes(EditorGroupId.ENVIRONMENT) &&
            renderEnvironment()}
          {visibleGroupIds.includes(EditorGroupId.SPOTS) && renderSpots()}
          {visibleGroupIds.includes(EditorGroupId.AI_SCRIPTS) && (
            <AiScriptsTreeSection />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SceneTree;
